/**
 * Phase B-3 — Admin Bank Rules editor. Tabbed alongside other Loan CMS tabs.
 * Lets admins maintain per-bank assessment rules without redeploys.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Plus, Pencil, Trash2, RefreshCw, Sliders } from 'lucide-react';

const BLANK = {
  code: '', name: '', active: true,
  min_monthly_income: 3000, max_dsr_percent: 70,
  max_dsr_percent_b40: 60, max_dsr_percent_t20: 75,
  max_tenure_years: 35, retirement_age: 70,
  max_ltv_percent: 90, min_ctos_score: 600,
  late_payments_tolerance: 1, stress_rate_buffer_percent: 1.0,
  nationalities_allowed: ['MY'],
  property_types_allowed: ['landed', 'apartment', 'commercial'],
  bumi_friendly: false, bumi_only: false,
  accept_probation: false, accept_newly_changed_job: false,
  accept_no_emp_verification: false, accept_pure_commission: true,
  accept_business_under_2y: false, accept_dcheqs: false,
  factors: { salaried_basic: 1.0, salaried_ot: 0.5, commission: 0.7, ssm_income: 0.7, rental_other: 0.7, sg_salaried_basic: 0.7, sg_salaried_ot: 0.4 },
  notes: '',
};

export default function BankRulesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/bank-rules'); setItems(r.data.items || []); }
    catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(BLANK); setShowForm(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...BLANK, ...b }); setShowForm(true); };
  const remove = async (b) => {
    if (!window.confirm(`Delete ${b.code}? This stops the engine using these rules.`)) return;
    try { await api.delete(`/admin/bank-rules/${b.code}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };
  const reseed = async () => {
    if (!window.confirm('Re-insert default rules for any missing bank? (Existing rules NOT overwritten)')) return;
    try {
      const r = await api.post('/admin/bank-rules/reseed?force=false');
      toast.success(`Reseed complete: ${r.data.inserted_or_replaced} new bank(s) added`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reseed failed'); }
  };
  const save = async () => {
    if (!form.code || !form.name) { toast.error('Code and Name required'); return; }
    try {
      const payload = { ...form };
      if (editing) await api.put(`/admin/bank-rules/${editing.code}`, payload);
      else await api.post('/admin/bank-rules', payload);
      toast.success('Saved'); setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const setFactor = (key, value) => setForm(prev => ({ ...prev, factors: { ...prev.factors, [key]: parseFloat(value) || 0 } }));

  return (
    <div className="space-y-3 mt-4" data-testid="bank-rules-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-gray-800 flex items-center gap-2"><Sliders className="w-4 h-4" /> Bank Assessment Rules ({items.length})</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reseed} data-testid="bank-rules-reseed"><RefreshCw className="w-4 h-4 mr-1" /> Re-seed defaults</Button>
          <Button onClick={openCreate} data-testid="bank-rules-add"><Plus className="w-4 h-4 mr-1" /> Add Bank Rule</Button>
        </div>
      </div>
      <p className="text-xs text-gray-500">These rules drive the Pre-Profile assessment engine. Income multipliers, DSR caps, CTOS floors, and eligibility flags are per-bank. Changes take effect immediately on the next assessment run.</p>

      <div className="border rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left p-2">Code</th>
              <th className="text-left p-2">Bank</th>
              <th className="text-right p-2">Min Income</th>
              <th className="text-right p-2">Max DSR</th>
              <th className="text-right p-2">Min CTOS</th>
              <th className="text-right p-2">Max Tenure</th>
              <th className="text-left p-2">Flags</th>
              <th className="text-left p-2">Status</th>
              <th className="text-right p-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-4 text-center text-gray-400">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-gray-400">No bank rules yet. Click "Re-seed defaults" to load the 8 default MY banks.</td></tr>
            )}
            {items.map(b => (
              <tr key={b.code} className="border-t" data-testid={`bank-rule-row-${b.code}`}>
                <td className="p-2 font-mono text-xs">{b.code}</td>
                <td className="p-2">{b.name}{b.notes ? <p className="text-xs text-gray-400">{b.notes}</p> : null}</td>
                <td className="p-2 text-right">RM {Number(b.min_monthly_income).toLocaleString()}</td>
                <td className="p-2 text-right">{b.max_dsr_percent}%</td>
                <td className="p-2 text-right">{b.min_ctos_score}</td>
                <td className="p-2 text-right">{b.max_tenure_years}y</td>
                <td className="p-2 text-xs">
                  {b.bumi_friendly && <Badge className="bg-amber-100 text-amber-700 mr-1 text-[10px]">bumi</Badge>}
                  {b.accept_probation && <Badge className="bg-blue-100 text-blue-700 mr-1 text-[10px]">probation OK</Badge>}
                  {b.accept_newly_changed_job && <Badge className="bg-violet-100 text-violet-700 mr-1 text-[10px]">new job OK</Badge>}
                </td>
                <td className="p-2"><Badge variant={b.active ? 'default' : 'outline'}>{b.active ? 'Active' : 'Inactive'}</Badge></td>
                <td className="p-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(b)} data-testid={`bank-rule-edit-${b.code}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(b)}><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit Bank Rule — ${editing.code}` : 'New Bank Rule'}</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              These values directly feed the assessment engine. Be conservative — banks update their criteria periodically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label>Code *</Label><Input value={form.code} disabled={!!editing} onChange={e => setField('code', e.target.value.toUpperCase())} data-testid="br-code" /></div>
              <div className="col-span-2"><Label>Bank Name *</Label><Input value={form.name} onChange={e => setField('name', e.target.value)} data-testid="br-name" /></div>
              <div><Label>Min Income (RM/mo)</Label><Input type="number" value={form.min_monthly_income} onChange={e => setField('min_monthly_income', parseFloat(e.target.value || 0))} /></div>
              <div><Label>Max DSR %</Label><Input type="number" value={form.max_dsr_percent} onChange={e => setField('max_dsr_percent', parseFloat(e.target.value || 0))} /></div>
              <div><Label>Min CTOS Score</Label><Input type="number" value={form.min_ctos_score} onChange={e => setField('min_ctos_score', parseInt(e.target.value || 0))} /></div>
              <div><Label>Max DSR % (B40)</Label><Input type="number" value={form.max_dsr_percent_b40 || ''} onChange={e => setField('max_dsr_percent_b40', parseFloat(e.target.value || 0))} /></div>
              <div><Label>Max DSR % (T20)</Label><Input type="number" value={form.max_dsr_percent_t20 || ''} onChange={e => setField('max_dsr_percent_t20', parseFloat(e.target.value || 0))} /></div>
              <div><Label>Late Payments Tolerance</Label><Input type="number" value={form.late_payments_tolerance} onChange={e => setField('late_payments_tolerance', parseInt(e.target.value || 0))} /></div>
              <div><Label>Max Tenure (years)</Label><Input type="number" value={form.max_tenure_years} onChange={e => setField('max_tenure_years', parseInt(e.target.value || 0))} /></div>
              <div><Label>Retirement Age</Label><Input type="number" value={form.retirement_age} onChange={e => setField('retirement_age', parseInt(e.target.value || 0))} /></div>
              <div><Label>Max LTV %</Label><Input type="number" value={form.max_ltv_percent} onChange={e => setField('max_ltv_percent', parseFloat(e.target.value || 0))} /></div>
              <div><Label>Stress-rate buffer %</Label><Input type="number" step="0.1" value={form.stress_rate_buffer_percent} onChange={e => setField('stress_rate_buffer_percent', parseFloat(e.target.value || 0))} /></div>
            </div>

            <div className="pt-3 border-t">
              <p className="font-semibold text-sm mb-2">Income Multipliers (haircuts)</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {Object.entries(form.factors).map(([k, v]) => (
                  <div key={k}>
                    <Label className="text-xs">{k.replace(/_/g, ' ')}</Label>
                    <Input type="number" step="0.05" min="0" max="1.5" value={v} onChange={e => setFactor(k, e.target.value)} data-testid={`br-factor-${k}`} />
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-3 border-t">
              <p className="font-semibold text-sm mb-2">Eligibility flags</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {[
                  ['bumi_friendly', 'Bumi-friendly (+5% LTV on bumi units)'],
                  ['bumi_only', 'Bumi-only bank'],
                  ['accept_probation', 'Accept on-probation applicants'],
                  ['accept_newly_changed_job', 'Accept newly-changed job'],
                  ['accept_no_emp_verification', 'Accept no employment verification'],
                  ['accept_pure_commission', 'Accept pure commission earners (no base)'],
                  ['accept_business_under_2y', 'Accept SSM business < 2 years old'],
                  ['accept_dcheqs', 'Accept applicants with DCHEQS'],
                  ['active', 'Active (use in assessments)'],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={!!form[k]} onCheckedChange={v => setField(k, !!v)} data-testid={`br-flag-${k}`} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="pt-3 border-t">
              <Label>Nationalities allowed (comma-separated, e.g. MY,PR,SG)</Label>
              <Input
                value={(form.nationalities_allowed || []).join(',')}
                onChange={e => setField('nationalities_allowed', e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean))}
              />
              <Label className="mt-2">Property types allowed</Label>
              <Input
                value={(form.property_types_allowed || []).join(',')}
                onChange={e => setField('property_types_allowed', e.target.value.split(',').map(s => s.trim().toLowerCase()).filter(Boolean))}
              />
              <Label className="mt-2">Notes</Label>
              <Textarea rows={2} value={form.notes || ''} onChange={e => setField('notes', e.target.value)} />
            </div>

            <Button onClick={save} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="br-save">
              {editing ? 'Update Rule' : 'Create Rule'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
